import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Err, Ok, Result } from 'ts-results'

import { useApplication, useNotifications } from 'app/application/application'
import { useApplicationStore } from 'app/application/infrastructure'
import { DataError, useApi } from 'app/common/application'

import {
  ChangeWorkspaceRequest,
  ChangeWorkspaceResponse,
  InvitedUserWorkspace,
  NewUserInviteContent,
  User,
  UserAuthRequest,
  UserAuthResponse,
  UserData,
  UserGetResponse,
  UserHandleInviteDecision,
  UserHandleInviteRequest,
  UserHandleInviteResponse,
  UserResetPasswordRequest,
  UserResetPasswordResponse,
  UserTriggerPasswordResetRequest,
  UserTriggerPasswordResetResponse,
  UserUpdateRequest,
  WorkspacesGetRequest,
  WorkspacesGetRespone,
} from '../types'

const mapUser = (loginData: UserData) =>
  ({
    workspaces: loginData.workspaces,
    isAdmin: loginData.isAdmin,
    email: loginData.email,
    firstName: loginData.firstName,
    lastName: loginData.lastName,
    userId: loginData.userId,
  }) satisfies User

const isInvitedUserWorkspace = (content: unknown): content is InvitedUserWorkspace =>
  (content as InvitedUserWorkspace).id !== undefined

const useUser = () => {
  const { create } = useApi()
  const { setAuthorization } = useApplication()
  const { createNotification } = useNotifications()
  const { t } = useTranslation('error')

  const resetApplicationState = useApplicationStore((state) => state.resetAll)
  const workspace = useApplicationStore((state) => state.workspace)
  const user = useApplicationStore((state) => state.user)
  const removeUserWorkspace = useApplicationStore((state) => state.removeUserWorkspace)
  const setWorkspace = useApplicationStore((state) => state.setWorkspace)
  const setUser = useApplicationStore((state) => state.setUser)
  const updateUserWorkspace = useApplicationStore((state) => state.updateUserWorkspace)

  const getWorkspace = useCallback(
    (workspaceId: string) =>
      create<WorkspacesGetRequest, WorkspacesGetRespone>('/core/workspaces/get', {
        content: {
          id: workspaceId,
        },
      }),
    [create],
  )

  const getWorkspaceInfomationAndStoreData = useCallback(
    (userData: UserData) => {
      const user = mapUser(userData)

      return getWorkspace(userData.selectedWorkspaceId).then((workspaceResult) => {
        if (workspaceResult.ok) {
          const workspaceData = workspaceResult.val.data
          const workspace = {
            apiKey: workspaceData.apikey,
            id: workspaceData.id,
            name: workspaceData.name,
          }

          setWorkspace(workspace)
          setUser(user)
          setAuthorization({
            isAdmin: user.isAdmin,
            state: 'valid',
          })

          return Ok(user)
        } else {
          return workspaceResult
        }
      })
    },
    [getWorkspace, setWorkspace, setAuthorization, setUser],
  )

  const changeWorkspace = useCallback(
    (
      workspaceId: string,
      onChange: (success: boolean) => Promise<void>,
    ): Promise<Result<User, DataError<number>>> =>
      create<ChangeWorkspaceRequest, ChangeWorkspaceResponse>('/core/user/switchWorkspace', {
        content: { workspaceId },
      }).then(async (result) => {
        await onChange(result.ok)
        if (result.ok) {
          return getWorkspaceInfomationAndStoreData(result.val.data)
        } else {
          createNotification({
            message: result.val.message,
            title: t(`http.${result.val.code}`),
            type: 'error',
          })

          return Err(result.val)
        }
      }),
    [create, createNotification, getWorkspaceInfomationAndStoreData, t],
  )

  const handleInvite = useCallback(
    (
      decision: UserHandleInviteDecision,
      input: NewUserInviteContent | InvitedUserWorkspace,
    ): Promise<Result<undefined, DataError<number>>> =>
      create<UserHandleInviteRequest, UserHandleInviteResponse>('/core/user/handleInvite', {
        content: {
          decision,
          ...(isInvitedUserWorkspace(input)
            ? {
                inviteCode: input.inviteCode,
                userId: user!.userId,
              }
            : input),
        },
      }).then((result) => {
        if (result.ok) {
          if (user && isInvitedUserWorkspace(input)) {
            if (result.val) {
              updateUserWorkspace(result.val.data)
            } else {
              removeUserWorkspace(input)
            }
          }
          return Ok(undefined)
        }

        return result
      }),
    [create, removeUserWorkspace, updateUserWorkspace, user],
  )

  const login = useCallback(
    (email: string, password: string): Promise<Result<User, DataError<number>>> =>
      create<UserAuthRequest, UserAuthResponse>('/client/v1/auth', {
        email: email.trim(),
        password,
      }).then((loginResult) =>
        loginResult.ok ? getWorkspaceInfomationAndStoreData(loginResult.val) : loginResult,
      ),
    [create, getWorkspaceInfomationAndStoreData],
  )

  const logout = useCallback(
    () => create('/auth/signout', {}).finally(() => resetApplicationState()),
    [create, resetApplicationState],
  )

  const resetPassword = useCallback(
    (password: string, uuid: string) =>
      create<UserResetPasswordRequest, UserResetPasswordResponse>('/core/user/resetPassword', {
        content: {
          password,
          uuid,
        },
      }).then((result) => (result.ok ? Ok(undefined) : result)),
    [create],
  )

  const triggerPasswordReset = useCallback(
    (email: string) =>
      create<UserTriggerPasswordResetRequest, UserTriggerPasswordResetResponse>(
        '/core/user/triggerPasswordReset',
        {
          content: {
            email: email.trim(),
          },
        },
      ).then((result) => (result.ok ? Ok(undefined) : result)),
    [create],
  )

  const update = useCallback(
    (
      firstName: string,
      lastName: string,
      password: string,
    ): Promise<Result<undefined, DataError<number>>> =>
      create<UserUpdateRequest, UserGetResponse>('/core/user/update', {
        content: { firstName, lastName, password },
      }).then((result) => (result.ok ? Ok(undefined) : result)),
    [create],
  )

  return {
    // user is legacy storage place, migrates over time
    workspace: (workspace ?? user?.workspace)!,
    changeWorkspace,
    handleInvite,
    login,
    logout,
    resetPassword,
    triggerPasswordReset,
    update,
    user,
  }
}

export default useUser
